import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Spinner } from 'react-bootstrap';
import '../pages/PatientReportView.css';

const SecureReportView = () => {
  const { encodedId } = useParams();
  const [report, setReport] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signedDoctors, setSignedDoctors] = useState([]);

  const formatTimeWithAMPM = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${period}`;
  };

  const getUrduTiming = (timing, dosage) => {
    const timingMap = {
      morning: "صبح",
      afternoon: "دوپہر",
      evening: "شام",
      night: "رات"
    };
    
    const beforeAfterMap = {
      before: "کھانے سے پہلے",
      after: "کھانے کے بعد",
      both: "کھانے سے پہلے/بعد",
      none: ""
    };
    
    if (!dosage) return timingMap[timing] || "";
    
    let before_after = 'none';
    
    if (timing === 'morning') {
      if (dosage.morningBeforeFood && dosage.morningAfterFood) before_after = 'both';
      else if (dosage.morningBeforeFood) before_after = 'before';
      else if (dosage.morningAfterFood) before_after = 'after';
    } else if (timing === 'afternoon') {
      if (dosage.afternoonBeforeFood && dosage.afternoonAfterFood) before_after = 'both';
      else if (dosage.afternoonBeforeFood) before_after = 'before';
      else if (dosage.afternoonAfterFood) before_after = 'after';
    } else if (timing === 'evening') {
      if (dosage.eveningBeforeFood && dosage.eveningAfterFood) before_after = 'both';
      else if (dosage.eveningBeforeFood) before_after = 'before';
      else if (dosage.eveningAfterFood) before_after = 'after';
    } else if (timing === 'night') {
      if (dosage.nightBeforeFood && dosage.nightAfterFood) before_after = 'both';
      else if (dosage.nightBeforeFood) before_after = 'before';
      else if (dosage.nightAfterFood) before_after = 'after';
    }
    
    if (before_after === 'none' && (dosage.beforeFood || dosage.afterFood)) {
      if (dosage.beforeFood && dosage.afterFood) before_after = 'both';
      else if (dosage.beforeFood) before_after = 'before';
      else if (dosage.afterFood) before_after = 'after';
    }
    
    return `${timingMap[timing] || ""} ${beforeAfterMap[before_after] || ""}`.trim();
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Decode the ID (Base64)
        const decodedId = atob(encodedId);
        
        console.log('Fetching report with encoded ID:', encodedId);
        const reportRes = await axios.get(`/api/reports/public/${decodedId}`);
        console.log('Report response:', reportRes.data);
        const reportData = {...reportRes.data};
        
        let allMedicines = [];
        try {
          const medicinesRes = await axios.get('/api/medicines');
          allMedicines = medicinesRes.data;
        } catch (err) {
          console.error("Error fetching medicines:", err);
        }
        
        if (reportData.medications && reportData.medications.length > 0) {
          reportData.medications = reportData.medications.map(med => {
            if (med.medicineName) return med;
            const medicineId = (typeof med.medicine === 'string') ? med.medicine : (med.medicine && med.medicine.id) ? med.medicine.id : null;
            if (medicineId) {
              const medicineDetails = allMedicines.find(m => m.id && m.id.toString() === medicineId.toString());
              if (medicineDetails) {
                return { ...med, medicineName: medicineDetails.name, medicineDetails };
              }
            }
            return med;
          });
        }
        
        setReport(reportData);
        
        const clinicRes = await axios.get('/api/clinic.php');
        setClinic(clinicRes.data);
        
        let doctorsData = [];
        if (reportData.signedByDoctors && reportData.signedByDoctors.length > 0) {
          for (const doctorId of reportData.signedByDoctors) {
            try {
              const docRes = await axios.get(`/api/doctors/${doctorId}`);
              doctorsData.push(docRes.data);
            } catch (error) {
              console.error("Error fetching doctor:", error);
            }
          }
        }
        
        setSignedDoctors(doctorsData);
        setLoading(false);
        
        // Auto-print after 1 second
        setTimeout(() => {
          window.print();
        }, 1000);
        
      } catch (err) {
        console.error('Error fetching report:', err);
        console.error('Error details:', err.response?.data);
        setError('Report not found or invalid link');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [encodedId]);

  const formatMedicationInstructions = (med) => {
    const timings = [];
    const { dosage } = med;
    if (!dosage) return "No dosage information";
    
    if (dosage.morning) {
      let ba = "";
      if (dosage.morningBeforeFood && dosage.morningAfterFood) ba = "before & after food";
      else if (dosage.morningBeforeFood) ba = "before food";
      else if (dosage.morningAfterFood) ba = "after food";
      else if (dosage.beforeFood && dosage.afterFood) ba = "before & after food";
      else if (dosage.beforeFood) ba = "before food";
      else if (dosage.afterFood) ba = "after food";
      timings.push(`Morning ${ba}`);
    }
    
    if (dosage.afternoon) {
      let ba = "";
      if (dosage.afternoonBeforeFood && dosage.afternoonAfterFood) ba = "before & after food";
      else if (dosage.afternoonBeforeFood) ba = "before food";
      else if (dosage.afternoonAfterFood) ba = "after food";
      else if (dosage.beforeFood && dosage.afterFood) ba = "before & after food";
      else if (dosage.beforeFood) ba = "before food";
      else if (dosage.afterFood) ba = "after food";
      timings.push(`Afternoon ${ba}`);
    }
    
    if (dosage.evening) {
      let ba = "";
      if (dosage.eveningBeforeFood && dosage.eveningAfterFood) ba = "before & after food";
      else if (dosage.eveningBeforeFood) ba = "before food";
      else if (dosage.eveningAfterFood) ba = "after food";
      else if (dosage.beforeFood && dosage.afterFood) ba = "before & after food";
      else if (dosage.beforeFood) ba = "before food";
      else if (dosage.afterFood) ba = "after food";
      timings.push(`Evening ${ba}`);
    }
    
    if (dosage.night) {
      let ba = "";
      if (dosage.nightBeforeFood && dosage.nightAfterFood) ba = "before & after food";
      else if (dosage.nightBeforeFood) ba = "before food";
      else if (dosage.nightAfterFood) ba = "after food";
      else if (dosage.beforeFood && dosage.afterFood) ba = "before & after food";
      else if (dosage.beforeFood) ba = "before food";
      else if (dosage.afterFood) ba = "after food";
      timings.push(`Night ${ba}`);
    }
    
    return timings.join(", ") || "No specific timing";
  };

  const formatMedicationInstructionsUrdu = (med) => {
    const timings = [];
    const { dosage } = med;
    if (!dosage) return "";
    
    if (dosage.morning) timings.push(getUrduTiming("morning", dosage));
    if (dosage.afternoon) timings.push(getUrduTiming("afternoon", dosage));
    if (dosage.evening) timings.push(getUrduTiming("evening", dosage));
    if (dosage.night) timings.push(getUrduTiming("night", dosage));
    
    return timings.join("، ") || "";
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: 'white' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px', backgroundColor: 'white', padding: '50px' }}>
        <h4>❌ {error || 'Report not found'}</h4>
        <p>Invalid or expired link</p>
      </div>
    );
  }

  const patientData = report.patient || report.Patient || { name: "Unknown", patientId: "N/A", age: "?", gender: "?", bloodGroup: "Unknown" };
  const doctorData = report.doctor || report.Doctor || { name: "Unknown Doctor" };
  const formattedDate = new Date(report.date).toLocaleDateString();
  const formattedTime = new Date(report.date).toLocaleTimeString();

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto', backgroundColor: 'white', minHeight: '100vh' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;700&display=swap');
        
        body {
          background-color: white !important;
        }
        
        .urdu-text {
          font-family: 'Noto Nastaliq Urdu', serif;
          font-size: 11px;
          text-align: right;
          direction: rtl;
          color: #333;
          margin-top: 2px;
          font-weight: 500;
        }
        
        .bi-lingual {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        @media print {
          @page { 
            size: A4; 
            margin: 15mm 10mm 20mm 10mm; 
          }
          body { 
            margin: 0; 
            padding: 0; 
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background-color: white !important;
          }
          .no-print { 
            display: none !important; 
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          h5, h6 {
            page-break-after: avoid;
          }
        }
      `}</style>
      
      {/* Print Button - Hidden on print */}
      <div className="no-print" style={{ textAlign: 'center', marginBottom: '20px' }}>
        <button 
          onClick={() => window.print()} 
          style={{ 
            padding: '10px 20px', 
            backgroundColor: '#007bff', 
            color: 'white', 
            border: 'none', 
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          🖨️ Print Report
        </button>
      </div>
      
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid #1a5276', paddingBottom: '10px' }}>
        <img 
          src={clinic?.logo ? `${window.location.origin}${clinic.logo}` : "/logo.png"} 
          alt="Clinic Logo" 
          style={{ width: '60px', marginRight: '15px' }} 
        />
        <div>
          <h2 style={{ margin: 0, color: '#1a5276' }}>{clinic?.name || "Clinic Name"}</h2>
          <p style={{ margin: 0, fontSize: '12px' }}>{clinic?.address || "Address"} | {clinic?.contactNumber || "Contact"}</p>
        </div>
      </div>
      
      {/* Patient Info */}
      <table style={{ width: '100%', marginBottom: '15px', fontSize: '14px' }}>
        <tbody>
          <tr>
            <td><strong>Patient ID:</strong> {patientData.patientId}</td>
            <td style={{ textAlign: 'right' }}><strong>Date:</strong> {formattedDate}</td>
          </tr>
          <tr>
            <td><strong>Patient Name:</strong> {patientData.name}</td>
            <td style={{ textAlign: 'right' }}><strong>Doctor:</strong> {doctorData.name}</td>
          </tr>
          <tr>
            <td><strong>Age/Gender:</strong> {patientData.age}/{patientData.gender}</td>
            <td style={{ textAlign: 'right' }}><strong>Time:</strong> {formattedTime}</td>
          </tr>
          <tr>
            <td><strong>Blood Group:</strong> {patientData.bloodGroup || 'None'}</td>
            <td style={{ textAlign: 'right' }}>
              {report.bloodPressure && report.bloodPressure.systolic && report.bloodPressure.diastolic && (
                <><strong>Blood Pressure:</strong> {report.bloodPressure.systolic}/{report.bloodPressure.diastolic} mmHg</>
              )}
            </td>
          </tr>
        </tbody>
      </table>
      
      {/* Diagnosis */}
      <div style={{ marginBottom: '15px' }}>
        <h5>Diagnosis</h5>
        <p>{report.diagnosis}</p>
        {report.doctorAdvice && (
          <>
            <h5>Doctor's Advice</h5>
            <p>{report.doctorAdvice}</p>
          </>
        )}
      </div>
      
      <hr />
      
      {/* Medications */}
      <h5 style={{ textAlign: 'center', marginTop: '20px' }}>Medications</h5>
      {report.medications && report.medications.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', border: '1px solid #ddd' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #ddd' }}>
              <th style={{ textAlign: 'left', padding: '8px', border: '1px solid #ddd' }}>Medicine</th>
              <th style={{ textAlign: 'left', padding: '8px', border: '1px solid #ddd' }}>Instructions</th>
              <th style={{ textAlign: 'left', padding: '8px', border: '1px solid #ddd' }}>Duration</th>
            </tr>
          </thead>
          <tbody>
            {report.medications.map((med, index) => (
              <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                  <strong>{med.medicineName || "Unknown"}</strong>
                </td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                  <div className="bi-lingual">
                    <div>{formatMedicationInstructions(med)}</div>
                    <div className="urdu-text">{formatMedicationInstructionsUrdu(med)}</div>
                  </div>
                  {med.instructions && <div style={{ fontSize: '11px', marginTop: '4px', color: '#666' }}>Note: {med.instructions}</div>}
                </td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{med.duration}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p style={{ textAlign: 'center' }}>No medications prescribed</p>
      )}
      
      {/* Doctor Signature */}
      <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'space-around', borderTop: '1px solid #ddd', paddingTop: '20px' }}>
        {signedDoctors.length > 0 ? (
          signedDoctors.map((doctor, index) => (
            <div key={index} style={{ textAlign: 'center', flex: '0 0 30%' }}>
              <div style={{ borderTop: '1px solid #000', paddingTop: '5px', marginBottom: '5px' }}></div>
              <p style={{ margin: '0', fontSize: '12px', fontWeight: 'bold' }}>Dr. {doctor.name}</p>
              <p style={{ margin: '0', fontSize: '11px', color: '#666' }}>{doctor.qualification}</p>
              {doctor.specialization && <p style={{ margin: '0', fontSize: '10px', color: '#888' }}>{doctor.specialization}</p>}
            </div>
          ))
        ) : (
          <div style={{ textAlign: 'center', flex: '0 0 30%' }}>
            <div style={{ borderTop: '1px solid #000', paddingTop: '5px', marginBottom: '5px' }}></div>
            <p style={{ margin: '0', fontSize: '12px', fontWeight: 'bold' }}>Dr. {doctorData.name}</p>
            <p style={{ margin: '0', fontSize: '11px', color: '#666' }}>{doctorData.qualification || 'MBBS'}</p>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '11px', color: '#666', borderTop: '1px solid #ddd', paddingTop: '10px' }}>
        <p style={{ margin: '5px 0' }}>
          <strong>Clinic Hours:</strong> {formatTimeWithAMPM(clinic?.timings?.open)} - {formatTimeWithAMPM(clinic?.timings?.close)}
        </p>
        <p style={{ margin: '5px 0', fontSize: '10px' }}>Designed by Abdullah Arif | Contact: +92-3137323866</p>
      </div>
    </div>
  );
};

export default SecureReportView;
